/**
 * transition-store — coordinates the splash → operator-selector → main-app
 * canvas morphs.
 *
 * The unified canvas (`StreamParticleCanvas`) lives at the App level and
 * reads its phase from this store:
 *
 *   - 'river'      Initial state. Flowing river is rendered, particles are dormant.
 *
 *   - 'morphing'   Click on the splash triggered the splash → operator selector
 *                  transition. Flow lines freeze and shed particles that scatter
 *                  and then spring toward the first idle abstract shape.
 *
 *   - 'particles'  The forward morph completed. Particles drift between
 *                  abstract shapes / spell operator initials. Used by the
 *                  OperatorSelector.
 *
 *   - 'unmorphing' Reverse of `morphing`: particles fly back to the river
 *                  path. Used when the user clicks "Switch operator".
 *
 *   ── Operator selector → main app shell flow ─────────────────────────────
 *
 *   These four phases drive the slick "particles trace the UI, then dissolve"
 *   reveal that happens after the operator clicks Continue. Each phase has a
 *   single visual job so the animation stays fluid:
 *
 *   - 'morphing-to-shell'      Particles fly from the orbital ring to the
 *                              traced positions of the destination shell
 *                              (sidebar outline, header outline, content area).
 *
 *   - 'shell-trace-hold'       Particles HOLD at their trace positions.
 *                              AppShell is hidden. This is the "trace moment"
 *                              the eye reads as "particles are perfectly
 *                              outlining the UI".
 *
 *   - 'shell-ui-revealing'     AppShell fades in BEHIND the still-visible
 *                              particles. The particles overlay the now-visible
 *                              UI, reading as "the UI is what the particles
 *                              were forming".
 *
 *   - 'shell-particles-fading' Particles dissolve to transparent, leaving
 *                              the AppShell as the final reality. Canvas
 *                              z-index drops back to baseline at the end.
 *
 *   - 'shell-revealed'         Terminal state. Canvas is idle and behind UI.
 */

import { create } from 'zustand'
import { IS_TAURI } from '@/lib/platform'

export type TransitionPhase =
  | 'river'
  | 'morphing'
  | 'particles'
  | 'unmorphing'
  | 'morphing-to-shell'
  | 'shell-trace-hold'
  | 'shell-ui-revealing'
  | 'shell-particles-fading'
  | 'shell-revealed'

/**
 * Total time from `triggerMorph()` to the canvas entering 'particles' phase.
 * Mirrored in StreamParticleCanvas.tsx — keep in sync if changed.
 *
 * 3000ms gives the morph a long, cinematic feel:
 *   - 0–1050ms   coast: particles glide at river speed with no spring force,
 *                 visually becoming a free-flowing particle field.
 *   - 1050–2700ms ramp: the spring builds over 1650ms — nearly 3× the old
 *                 ramp width — so particles arc gracefully into formation
 *                 rather than snapping. The curl force creates visible arcs
 *                 during this entire window.
 *   - 2700–3000ms settle: full spring, particles lock into their targets.
 *
 * Compare with UNMORPH_DURATION_MS = 1700ms, which has a similar coast +
 * travel + flow structure.
 */
export const MORPH_DURATION_MS = 3000

/**
 * Total time from `triggerUnmorph()` to the canvas entering 'river' phase.
 * Slightly longer than the forward morph so users can clearly see particles
 * flying toward exact points on the river path before the river materialises.
 */
export const UNMORPH_DURATION_MS = 1700

// ── Shell morph timings ─────────────────────────────────────────────────────
// Each phase is sequenced: when one ends, the next begins. Tuning any of
// these shifts the whole timeline.

/** Particles fly from orbital → trace positions. */
export const SHELL_MORPH_MS = 1200

/** Particles HOLD at trace positions, UI still hidden. The "money shot". */
export const SHELL_HOLD_MS = 400

/** AppShell fades in behind the still-visible particles. */
export const SHELL_UI_FADE_MS = 500

/** Particles dissolve to transparent, revealing AppShell underneath. */
export const SHELL_PARTICLES_FADE_MS = 600

/** Total perceived duration: ~2.7s from "Continue" click to fully revealed. */
export const SHELL_TOTAL_MS =
  SHELL_MORPH_MS + SHELL_HOLD_MS + SHELL_UI_FADE_MS + SHELL_PARTICLES_FADE_MS

interface TransitionState {
  phase: TransitionPhase
  /** Latest particle text target (operator initials, or 'Kodeon' on welcome). */
  particleText: string | null
  /**
   * Optional: route the shell morph is heading to. Set by the trigger so
   * components like AppShell know which page is being revealed.
   */
  shellTargetRoute: string | null
  /**
   * Whether the canvas should render particles at all. The splash and
   * welcome page set this to true; routes that take over the main content
   * area (events, customers, etc.) set it to false on mount so the canvas
   * fades to fully transparent and stays out of the way until the user
   * returns to a particle-driven view.
   */
  particlesVisible: boolean
  /**
   * Whether the SplashScreen is currently mounted. The flowing river is the
   * splash's signature visual and must NEVER appear behind the main app —
   * even if `phase` somehow ends up at 'river' (HMR, store reset, edge
   * cases). The canvas reads this flag and refuses to render the river
   * unless the splash is mounted, which structurally ties the river to its
   * one legitimate context. SplashScreen sets this true on mount, false on
   * unmount.
   */
  splashActive: boolean
  /** Begin the river → particles morph. Idempotent. */
  triggerMorph: () => void
  /** Begin the particles → river reverse morph. Idempotent. */
  triggerUnmorph: () => void
  /**
   * Begin the operator-selector → main-shell morph. Sequences through the
   * four shell-* phases automatically. Idempotent.
   *
   * `targetRoute` is informational — used by AppShell / DOM tracing to know
   * what page is being revealed.
   */
  triggerShellMorph: (targetRoute: string) => void
  /** Push the latest particle text target. Cleared by passing null. */
  setParticleText: (text: string | null) => void
  /**
   * Show or hide the particle canvas. WelcomePage flips this to true on
   * mount and false on unmount; routes don't need to interact with it.
   * The canvas applies a 600ms opacity fade either way so users don't
   * see particles snap on/off.
   */
  setParticlesVisible: (visible: boolean) => void
  /**
   * Set by SplashScreen on mount/unmount. Used by the canvas to gate the
   * river render so it can NEVER bleed into the main app.
   */
  setSplashActive: (active: boolean) => void
  /** Restore the initial 'river' phase. Not used in the normal flow but available for tests / dev. */
  reset: () => void
}

export const useTransitionStore = create<TransitionState>((set, get) => ({
  // The browser portal has no splash, so there is no river to morph from: it
  // starts in the settled particle state that the morph would have ended in.
  phase: IS_TAURI ? 'river' : 'particles',
  particleText: null,
  shellTargetRoute: null,
  // Default true so the splash, operator selector, and welcome flow all see
  // particles unless something explicitly hides them.
  particlesVisible: true,
  // Default false. SplashScreen flips this to true on mount; the canvas's
  // 'river' render branch is gated on it so the river only ever appears
  // under the splash. There is a brief window on the very first paint
  // where SplashScreen has rendered but its useLayoutEffect has not yet
  // run; the canvas's first frame would see splashActive=false and skip
  // the river. The next frame (after the layout effect) renders normally.
  // This is preferable to the inverse race where the river leaks into the
  // main app.
  splashActive: false,

  triggerMorph: () => {
    if (get().phase !== 'river') return // already morphing or finished
    set({ phase: 'morphing' })
    window.setTimeout(() => {
      // Guard against the unlikely case that something reset us mid-morph.
      if (get().phase === 'morphing') {
        set({ phase: 'particles' })
      }
    }, MORPH_DURATION_MS)
  },

  triggerUnmorph: () => {
    if (get().phase !== 'particles') return // only unmorph from particles phase
    set({ phase: 'unmorphing', particleText: null })
    window.setTimeout(() => {
      // Guard against the unlikely case that something interrupted mid-unmorph.
      if (get().phase === 'unmorphing') {
        set({ phase: 'river' })
      }
    }, UNMORPH_DURATION_MS)
  },

  triggerShellMorph: (targetRoute) => {
    const phase = get().phase
    // Shell morph can be entered from any "post-river" steady state. In the
    // normal flow that's 'particles' (after operator selector) but on a
    // returning-user reload it's whatever the splash dismiss left us in.
    if (
      phase === 'morphing-to-shell' ||
      phase === 'shell-trace-hold' ||
      phase === 'shell-ui-revealing' ||
      phase === 'shell-particles-fading' ||
      phase === 'shell-revealed'
    ) {
      // Already in or past the shell morph — don't restart it.
      return
    }
    set({
      phase: 'morphing-to-shell',
      shellTargetRoute: targetRoute,
      particleText: null,
    })
    // Sequence through the four shell phases. Each timeout is anchored to
    // the start of the morph, so cumulative drift doesn't compound.
    const t0 = performance.now()
    const advance = (next: TransitionPhase, atMs: number) => {
      window.setTimeout(
        () => {
          // Bail if a competing transition has taken over.
          const cur = get().phase
          const validPriors: Record<TransitionPhase, TransitionPhase | null> = {
            river: null,
            morphing: null,
            particles: null,
            unmorphing: null,
            'morphing-to-shell': 'morphing-to-shell',
            'shell-trace-hold': 'morphing-to-shell',
            'shell-ui-revealing': 'shell-trace-hold',
            'shell-particles-fading': 'shell-ui-revealing',
            'shell-revealed': 'shell-particles-fading',
          }
          if (cur !== validPriors[next]) return
          set({ phase: next })
        },
        Math.max(0, atMs - (performance.now() - t0)),
      )
    }
    advance('shell-trace-hold', SHELL_MORPH_MS)
    advance('shell-ui-revealing', SHELL_MORPH_MS + SHELL_HOLD_MS)
    advance(
      'shell-particles-fading',
      SHELL_MORPH_MS + SHELL_HOLD_MS + SHELL_UI_FADE_MS,
    )
    advance('shell-revealed', SHELL_TOTAL_MS)
  },

  setParticleText: (text) => {
    if (get().particleText === text) return
    set({ particleText: text })
  },

  setParticlesVisible: (visible) => {
    if (get().particlesVisible === visible) return
    set({ particlesVisible: visible })
  },

  setSplashActive: (active) => {
    if (get().splashActive === active) return
    set({ splashActive: active })
  },

  reset: () =>
    set({
      phase: 'river',
      particleText: null,
      shellTargetRoute: null,
      particlesVisible: true,
      splashActive: false,
    }),
}))
